import { useQuery } from '@tanstack/react-query';
import { Calendar, CircleOff, Lock, Package, Search } from 'lucide-react';

import { Alert, AlertContent, AlertDescription, AlertTitle } from '@/components/Alert/Alert';
import { Card } from '@/components/Card/Card';
import { Combobox } from '@/components/Combobox/Combobox';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import type { BrandIntelligence, ReportQuarter } from '@/features/reports/api';
import { BrandCharts } from '@/features/reports/brands/BrandCharts';
import { BrandTiles } from '@/features/reports/brands/BrandTiles';
import { previousLabel, quarterHint } from '@/features/reports/brands/labels';
import { BRANDS_COPY as COPY } from '@/features/reports/copy';
import { ReportFailure } from '@/features/reports/parts';
import { brandIntelligenceQuery, reportBrandsQuery } from '@/features/reports/queries';
import { revealChoice } from '@/features/reports/reveal';
import { BRAND_REPORT_STATE } from '@/features/reports/tabs';
import { ApiError } from '@/lib/api';

/**
 * Reports › Brand intelligence (ALS-91): one brand at a time, chosen from the register, for one
 * quarter. Not scoped by the page's relationship manager: it is the brand's standing across the
 * whole clientele, and every figure is an aggregate that names no client. A brand with nothing
 * observed gets a sentence saying why, never an estimate. There is no brand until one is chosen:
 * the register holds thousands, so guessing one would only be a report nobody asked for.
 */
export function BrandsPanel({
    brandId,
    quarter,
    onBrandChange,
    onQuarterChange,
}: {
    brandId: number | undefined;
    /** "2026-Q3"; absent is the current quarter. */
    quarter: string | undefined;
    onBrandChange: (brandId: number | undefined) => void;
    onQuarterChange: (quarter: string | undefined) => void;
}) {
    const controls = useQuery(reportBrandsQuery);
    const report = useQuery(
        brandIntelligenceQuery(brandId === undefined ? undefined : { brandId, quarter }),
    );
    const quarters = controls.data?.quarters ?? [];
    // A quarter in the address the segment does not offer, a hand-edited 1990-Q1, is still asked
    // for, and the report says what it is; the segment then has nothing lit.
    const segment = quarter ?? quarters[0]?.key;
    const chosen =
        quarters.find((q) => q.key === segment) ??
        (report.data?.quarter.key === segment ? report.data?.quarter : undefined);

    const options = (controls.data?.brands ?? []).map((b) => ({
        value: String(b.id),
        label: b.name,
    }));
    // A brand in the address that is not on the register still asks; the API says if it is gone.
    if (brandId !== undefined && !options.some((o) => o.value === String(brandId))) {
        options.push({ value: String(brandId), label: `${COPY.brand} ${brandId}` });
    }

    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
                {controls.isPending ? (
                    <Skeleton className="h-9 w-full rounded-md sm:w-[280px]" />
                ) : (
                    <Combobox
                        search
                        aria-label={COPY.brand}
                        value={brandId === undefined ? null : String(brandId)}
                        onValueChange={(next) =>
                            onBrandChange(next === null ? undefined : Number(next))
                        }
                        options={options}
                        placeholder={COPY.searchBrands}
                        searchPlaceholder={COPY.searchBrands}
                        emptyText={COPY.noBrand}
                        className="w-full sm:w-[280px]"
                    />
                )}
                {segment && quarters.length > 0 ? (
                    <QuarterControl
                        quarters={quarters}
                        value={segment}
                        onChange={(next) =>
                            onQuarterChange(next === quarters[0]?.key ? undefined : next)
                        }
                    />
                ) : null}
                {chosen ? (
                    <span className="text-muted-foreground text-xs">
                        {quarterHint(chosen, quarters)}
                    </span>
                ) : null}
            </div>

            {brandId === undefined ? (
                <Card floating>
                    <EmptyState
                        icon={<Search />}
                        title={COPY.choose.title}
                        body={COPY.choose.body}
                    />
                </Card>
            ) : report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex min-w-0 flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        {Array.from({ length: 7 }, (_, i) => (
                            <Skeleton key={i} className="h-[96px] rounded-lg" />
                        ))}
                    </div>
                    {/* The charts' heights, so the page does not jump when they land. */}
                    <div className="grid gap-(--layout-row-gap) lg:grid-cols-3">
                        {Array.from({ length: 3 }, (_, i) => (
                            <Skeleton key={i} className="h-[240px] rounded-lg" />
                        ))}
                    </div>
                    <Skeleton className="h-[200px] rounded-lg" />
                </div>
            ) : report.error instanceof ApiError && report.error.status === 404 ? (
                // Not a failure to retry: the brand is gone, and asking again cannot bring it back.
                <Card floating>
                    <EmptyState
                        icon={<CircleOff />}
                        title={COPY.gone.title}
                        body={COPY.gone.body}
                    />
                </Card>
            ) : report.isError && !report.data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void report.refetch()} />
            ) : report.data ? (
                <BrandReport report={report.data} quarters={quarters} />
            ) : null}
        </div>
    );
}

function QuarterControl({
    quarters,
    value,
    onChange,
}: {
    quarters: readonly ReportQuarter[];
    value: string;
    onChange: (quarter: string) => void;
}) {
    return (
        <SegmentedControl
            tone="solid"
            aria-label={COPY.quarter}
            value={value}
            onValueChange={onChange}
            className="max-w-full overflow-x-auto"
        >
            {quarters.map((q) => (
                <SegmentedControlItem
                    key={q.key}
                    value={q.key}
                    onClick={revealChoice}
                    className="shrink-0 pointer-coarse:min-w-11"
                >
                    {q.label}
                </SegmentedControlItem>
            ))}
        </SegmentedControl>
    );
}

function BrandReport({
    report,
    quarters,
}: {
    report: BrandIntelligence;
    quarters: readonly ReportQuarter[];
}) {
    if (report.state === BRAND_REPORT_STATE.Report && report.demand && report.commercial) {
        return (
            <>
                {/* The privacy note is a promise about the figures below it, so it goes with them. */}
                <Alert>
                    <Lock aria-hidden />
                    <AlertContent>
                        <AlertTitle>{COPY.privacy.title}</AlertTitle>
                        <AlertDescription>{COPY.privacy.body}</AlertDescription>
                    </AlertContent>
                </Alert>
                <BrandTiles
                    demand={report.demand}
                    commercial={report.commercial}
                    previous={previousLabel(report.quarter, quarters)}
                />
                {report.charts ? <BrandCharts charts={report.charts} /> : null}
            </>
        );
    }

    const first = quarters.find((q) => q.isFirst)?.label ?? null;
    const empty =
        report.state === BRAND_REPORT_STATE.BeforeData
            ? {
                  icon: <Calendar />,
                  title: COPY.states.beforeData.title(report.quarter.label),
                  body: COPY.states.beforeData.body(first),
              }
            : report.state === BRAND_REPORT_STATE.NotStocked
              ? {
                    icon: <Package />,
                    title: COPY.states.notStocked.title,
                    body: COPY.states.notStocked.body(report.brand),
                }
              : {
                    icon: <CircleOff />,
                    title: COPY.states.notEnoughActivity.title,
                    body: COPY.states.notEnoughActivity.body(
                        report.pieces,
                        report.brand,
                        report.quarter.label,
                    ),
                };
    return (
        <Card floating>
            <EmptyState icon={empty.icon} title={empty.title} body={empty.body} />
        </Card>
    );
}
